import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { IconButton } from "@mui/material";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { useGetAssessmentQuery, useSubmitAssessmentMutation } from "../../api/personalApi";
import { SoftButton, SoftFooter, cls } from "../hatim/soft/SoftKit";
import { todayString } from "../../lib/muzakereWeek";
import {
  OPEN_ANSWER_MAX,
  allAnswered,
  answerText,
  boolLabels,
  buildAnswers,
  correctAnswerText,
  effectiveState,
  isAnswered,
  patchAnswer,
  scoreText,
  verdictOf,
  withCorrectAnswers,
} from "../../lib/assessment";
import "../profile/darkSurface.css";
import ConnectionState from "../../components/ui/ConnectionState";

function Center({ Icon, text, children }) {
  return (
    <div className="flex flex-col items-center gap-3 py-14 px-6 text-center">
      {Icon ? <Icon className={cls.muted} /> : null}
      <p className={`text-sm ${cls.muted}`}>{text}</p>
      {children}
    </div>
  );
}

/** Gönderilmiş değerlendirme: puan, her soruda verilen cevap ve doğru cevap. */
function ResultView({ data, t }) {
  const response = data.myResponse;
  const questions = withCorrectAnswers(data.questions || [], response.answerKey);
  const byQid = new Map((response.answers || []).map((a) => [a.qid, a]));
  return (
    <>
      <div className={`px-5 py-4 ${cls.summary}`}>
        <div className={`text-xs ${cls.muted}`}>{t("assess_your_result")}</div>
        <div className={`text-2xl font-medium ${cls.heading}`} data-testid="assess-score">
          {scoreText(response.score, t)}
        </div>
      </div>
      <ul>
        {questions.map((q, i) => {
          const a = byQid.get(q.qid);
          const verdict = verdictOf(q, a, response.answerKey);
          const correct = correctAnswerText(q, t);
          return (
            <li key={q.qid} className={`px-5 py-3 flex flex-col gap-1 ${cls.row}`}>
              <span className={`text-sm ${cls.title}`}>{`${i + 1}. ${q.text}`}</span>
              <span className={`flex items-center gap-1.5 text-xs ${cls.muted}`}>
                {verdict === null ? null : verdict ? (
                  <CheckCircleIcon sx={{ fontSize: 15 }} className="text-[#2E9E6A] dark:text-[#7BE3A8]" />
                ) : (
                  <CancelIcon sx={{ fontSize: 15 }} className={cls.danger} />
                )}
                {`${t("assess_your_answer")}: ${answerText(q, a, t) || t("assess_unanswered")}`}
              </span>
              {correct ? (
                <span className="text-xs text-[#2E9E6A] dark:text-[#7BE3A8]">{`${t("assess_correct_answer")}: ${correct}`}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * Müzakere değerlendirmesi (mobil muzakere/assessment/[id]): sorular tek tek
 * (İleri / Geri), sonda gözden geçir ve gönder; gönderilince puan ve her
 * sorunun doğru cevabı. Açılmamış ya da kapanmış değerlendirmede durum yazılır.
 */
export default function AssessmentPage() {
  const { id } = useParams();
  const { t } = useScriptMode();
  const toast = useToast();
  const navigate = useNavigate();
  const { data: resp, isLoading, isError, error, refetch } = useGetAssessmentQuery({ muzakereId: id }, { skip: !id });
  const [submit, { isLoading: submitting }] = useSubmitAssessmentMutation();
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [submitted, setSubmitted] = useState(null);

  const data = useMemo(() => {
    const d = resp?.data;
    if (!d) return null;
    return { ...d, myResponse: submitted || d.myResponse || null, questions: d.questions || [] };
  }, [resp, submitted]);

  const questions = data?.questions || [];
  const state = data ? effectiveState(data.state, data.closesAt) : "none";
  const answering = !!data && !data.myResponse && state === "open" && questions.length > 0;
  const onReview = index >= questions.length;
  const goBack = () => navigate("/uygulama/profil");

  const handleSubmit = async () => {
    if (!allAnswered(questions, answers) || submitting) return;
    const result = await submit({ muzakereId: id, answers: buildAnswers(questions, answers), today: todayString() });
    if (result.error) {
      toast.error(result.error.message || t("assess_submit_failed"));
      // Zaten gönderilmiş ya da kapanmışsa güncel durum gösterilir.
      if (result.error.status === 409) refetch();
      return;
    }
    toast.success(t("assess_submitted"));
    const myResponse = result.data?.data?.myResponse;
    if (myResponse) setSubmitted(myResponse);
    else refetch();
  };

  let body;
  let footer = null;
  if (!data) {
    body = isLoading ? (
      <div className="p-5 flex flex-col gap-3">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`h-5 rounded ${cls.skeleton}`} />
        ))}
      </div>
    ) : isError ? (
      <ConnectionState error={error} onRetry={refetch} message={t("assess_load_failed")} />
    ) : (
      <Center text={t("assess_none")}>
        <SoftButton tone="soft" onClick={() => refetch()}>
          {t("retry")}
        </SoftButton>
      </Center>
    );
  } else if (data.myResponse) {
    body = <ResultView data={data} t={t} />;
  } else if (!answering) {
    body = (
      <Center
        Icon={state === "ready" ? LockOutlinedIcon : state === "closed" ? ScheduleIcon : AssignmentOutlinedIcon}
        text={state === "ready" ? t("assess_not_open") : state === "closed" ? t("assess_closed") : t("assess_none")}
      >
        {state === "ready" && data.questionCount ? (
          <span className={`text-xs ${cls.muted}`}>{`${data.questionCount} ${t("assess_question_word")}`}</span>
        ) : null}
      </Center>
    );
  } else {
    const q = questions[Math.min(index, questions.length - 1)];
    const current = answers[q.qid];
    const canNext = onReview ? allAnswered(questions, answers) : isAnswered(q, current);
    const set = (patch) => setAnswers((prev) => patchAnswer(prev, q.qid, patch));

    body = (
      <>
        <div className="flex justify-center gap-1.5 pt-4" aria-hidden>
          {[...questions.map((x) => x.qid), "review"].map((key, i) => (
            <span
              key={key}
              className={`w-1.5 h-1.5 rounded-full ${
                i === index
                  ? "bg-terakkiPrimaryDark dark:bg-[#8FC9CA]"
                  : i < index
                    ? "bg-terakkiBrand dark:bg-white/45"
                    : "bg-terakkiBrandMist dark:bg-white/15"
              }`}
            />
          ))}
        </div>
        {onReview ? (
          <div>
            <div className="px-5 pt-4 pb-2">
              <div className={`text-base font-medium ${cls.title}`}>{t("assess_review")}</div>
              <p className={`text-xs ${cls.muted}`}>{t("assess_review_hint")}</p>
            </div>
            <ul>
              {questions.map((question, i) => {
                const text = answerText(question, answers[question.qid], t);
                return (
                  <li key={question.qid}>
                    <button
                      type="button"
                      onClick={() => setIndex(i)}
                      disabled={submitting}
                      className={`w-full text-left px-5 py-3 flex flex-col gap-0.5 ${cls.row} ${cls.rowHover}`}
                    >
                      <span className={`text-sm ${cls.title}`}>{`${i + 1}. ${question.text}`}</span>
                      <span className={`text-xs line-clamp-2 ${text ? cls.muted : cls.danger}`}>
                        {text || t("assess_unanswered")}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="px-5 py-4 flex flex-col gap-3">
            {index === 0 ? <p className={`text-xs ${cls.muted}`}>{t("assess_open_today")}</p> : null}
            <span className={`text-xs ${cls.muted}`}>{`${t("assess_question")} ${index + 1}/${questions.length}`}</span>
            <p className={`text-lg leading-7 ${cls.title}`}>{q.text}</p>
            {q.type === "coktan" ? (
              <ul role="radiogroup" aria-label={q.text}>
                {(q.options || []).map((option, i) => {
                  const active = current?.choice === i;
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => set({ choice: i })}
                        className={`w-full flex items-center gap-3 py-3 text-left ${cls.row} ${cls.rowHover}`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                            active ? "border-terakkiPrimaryDark dark:border-[#8FC9CA]" : "border-terakkiBorder dark:border-white/45"
                          }`}
                        >
                          {active ? <span className="w-2.5 h-2.5 rounded-full bg-terakkiPrimaryDark dark:bg-[#8FC9CA]" /> : null}
                        </span>
                        <span className={`text-sm ${cls.title}`}>{option}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : q.type === "acik" ? (
              <textarea
                value={current?.text || ""}
                onChange={(e) => set({ text: e.target.value })}
                placeholder={t("assess_write_answer")}
                maxLength={OPEN_ANSWER_MAX}
                rows={5}
                className={`w-full rounded-xl border border-terakkiBorder bg-terakkiSurface p-3 text-sm text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`}
              />
            ) : (
              <div className="flex gap-2.5">
                {boolLabels(q.type).map((key, i) => {
                  const value = i === 0;
                  const active = current?.bool === value;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => set({ bool: value })}
                      className={`flex-1 py-2.5 rounded-full border text-sm transition-colors ${
                        active
                          ? "bg-terakkiPrimaryDark border-terakkiPrimaryDark text-white dark:bg-[#8FC9CA] dark:border-[#8FC9CA] dark:text-[#123B3D]"
                          : `border-terakkiBorder dark:border-white/[0.14] ${cls.title} ${cls.rowHover}`
                      }`}
                    >
                      {t(key)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </>
    );

    footer = (
      <SoftFooter>
        <SoftButton
          tone={index === 0 ? "cancel" : "soft"}
          fullWidth
          disabled={submitting}
          onClick={() => (index === 0 ? goBack() : setIndex((i) => i - 1))}
        >
          {index === 0 ? t("assess_give_up") : t("back")}
        </SoftButton>
        <SoftButton
          tone={onReview ? "success" : "primary"}
          fullWidth
          disabled={!canNext || submitting}
          endIcon={onReview ? <CheckIcon /> : <ChevronRightIcon />}
          onClick={() => (onReview ? handleSubmit() : setIndex((i) => i + 1))}
        >
          {onReview ? t("assess_submit") : t("next")}
        </SoftButton>
      </SoftFooter>
    );
  }

  return (
    <div className="tk-dark max-w-2xl mx-auto w-full">
      <div className={`${cls.panel} overflow-hidden flex flex-col max-h-[calc(100vh-3rem)] min-h-[360px]`}>
        <div className={`shrink-0 px-3 py-3 flex items-center gap-2 ${cls.summary}`}>
          <IconButton size="small" onClick={goBack} aria-label={t("back")}>
            <ArrowBackIcon fontSize="small" className={cls.accent} />
          </IconButton>
          <AssignmentOutlinedIcon fontSize="small" className={cls.accent} aria-hidden />
          <h1 className={`text-lg font-medium ${cls.heading}`}>{t("assess_title")}</h1>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">{body}</div>
        {footer}
      </div>
    </div>
  );
}
